import { ReactComponent as ChevronSVG } from "assets/icons/chevron.svg";
import { useCallback, useMemo, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import { TransitionProps } from "react-transition-group/Transition";
import { notNullNorUndefined } from "shared/helpers/notNullNorUndefined";

const DEFAULT_CHEVRON_SIZE = 14;

/**
 * `$transitionShortMedium` in `transition` value (Accordion.scss)
 */
const TIMEOUTS: TransitionProps["timeout"] = {
  enter: 450,
  appear: 0,
  exit: 0,
};

interface AccordionProps {
  initialOpen?: boolean;
  withArrow?: boolean;
  /**
   * 1-based level for how many sub accordions deep this accordion is
   */
  subLevel?: number;
  disabled?: boolean;
  headerChildren: React.ReactNode;
  additionalHeaderClasses?: string;
  bodyChildren: React.ReactNode;
  additionalBodyClasses?: string;
  mountOnEnter?: TransitionProps["mountOnEnter"];
  unmountOnExit?: TransitionProps["unmountOnExit"];
}

const Accordion: React.FC<AccordionProps> = ({
  initialOpen = false,
  withArrow = false,
  subLevel,
  disabled = false,
  headerChildren,
  additionalHeaderClasses = "",
  bodyChildren,
  additionalBodyClasses = "",
  mountOnEnter = false,
  unmountOnExit = false,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(initialOpen);
  const toggle = useCallback(() => {
    if (!disabled) {
      setOpen((o) => !o);
    }
  }, [disabled]);

  // NOTE: Only chevron size is decreased for sub-levels
  // We don't add additional margin/padding to `AccordionBody` because not all
  // components using the `SubAccordion` behaviour will need it.
  const chevronSize = useMemo(
    () => DEFAULT_CHEVRON_SIZE - 2 * (subLevel ?? 0),
    [subLevel]
  );
  const toggleIconStyle = useMemo<React.CSSProperties>(
    () => ({
      width: chevronSize,
      marginRight: DEFAULT_CHEVRON_SIZE - chevronSize,
    }),
    [chevronSize]
  );

  return (
    <div
      className={`Accordion ${
        notNullNorUndefined(subLevel) ? "SubAccordion" : ""
      }`}
    >
      <div
        className={`AccordionHeader ${additionalHeaderClasses} ${
          disabled ? "NoHover" : ""
        }`}
        onClick={toggle}
      >
        {headerChildren}
        {withArrow && (
          <div className="ToggleIcon" style={toggleIconStyle}>
            <ChevronSVG
              height={chevronSize}
              width={chevronSize}
              className={`ToggleIconInner ${open ? "Up" : ""}`}
              role="button"
              aria-label="Toggle accordion"
            />
          </div>
        )}
      </div>
      <Transition
        nodeRef={nodeRef}
        in={open}
        timeout={TIMEOUTS}
        mountOnEnter={mountOnEnter}
        unmountOnExit={unmountOnExit}
      >
        {(transitionState) => (
          <div
            ref={nodeRef}
            className={`AccordionBody ${additionalBodyClasses} ${transitionState}`}
          >
            {bodyChildren}
          </div>
        )}
      </Transition>
    </div>
  );
};

export default Accordion;
